// src/public/ChestionarAngajatPage.tsx
//
// The staff satisfaction questionnaire, opened from the SMS link.
//
// Unlike the patient questionnaire this talks to Supabase directly, through
// two SECURITY DEFINER functions keyed on the token (homesic-api mig 404).
// There is no session to mint: the token is already the credential, and the
// submit has to write the answers and cut the tie to the person in one
// transaction — routing that through another service would split the one
// operation that must not be split.
//
// Nothing on this page identifies the employee, and nothing is sent back that
// could. The answers carry only funcție, departament and the reporting month,
// all of which the server reads from the link.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../utils/supabaseClient'
import UiCopyBlock from '../components/UiCopyBlock'

type QuestionType = 'text' | 'yesno' | 'rating' | 'nps' | 'multichoice'

interface Question {
  id: string
  position: number
  type: QuestionType
  label: string
  options: string[]
  is_required: boolean
  allow_multiple_selection: boolean
}

interface Questionnaire {
  status: 'ok' | 'not_found' | 'used' | 'expired'
  hospital_name?: string
  show_logo?: boolean
  title?: string | null
  description?: string | null
  top_text?: string | null
  bottom_text?: string | null
  questions?: Question[]
}

interface SubmitResult {
  status: 'ok' | 'not_found' | 'used' | 'expired' | 'empty'
  answers_saved?: number
  title?: string | null
  description?: string | null
  top_text?: string | null
  bottom_text?: string | null
}

const NOT_OK_COPY: Record<string, { title: string; body: string }> = {
  not_found: {
    title: 'Link invalid',
    body: 'Linkul nu a putut fi găsit. Verifică dacă l-ai copiat complet din mesaj.',
  },
  used: {
    title: 'Chestionar deja completat',
    body: 'Acest chestionar a fost deja trimis. Îți mulțumim pentru răspunsuri.',
  },
  expired: {
    title: 'Link expirat',
    body: 'Perioada de completare s-a încheiat. Dacă dorești totuși să răspunzi, contactează departamentul Runos.',
  },
}

export default function ChestionarAngajatPage() {
  const { token = '' } = useParams<{ token: string }>()

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<Questionnaire | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [unsubscribe, setUnsubscribe] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // Synchronous double-submit guard — `submitting` state is async, so a
  // second tap in the ~16ms before React flushes still races through.
  // The second call gets status='used' server-side and would downgrade
  // the thank-you page to "Chestionar deja completat".
  const inFlight = useRef(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [done, setDone] = useState<SubmitResult | null>(null)
  const [missing, setMissing] = useState<string[]>([])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const { data: res, error } = await supabase.rpc('empfb_get_public_questionnaire', {
        p_token: token,
      })
      if (cancelled) return
      if (error) {
        setLoadError('Nu am putut încărca chestionarul. Încearcă din nou peste câteva momente.')
      } else {
        setData(res as Questionnaire)
      }
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [token])

  const questions = useMemo(() => data?.questions ?? [], [data])

  const setAnswer = useCallback((id: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [id]: value }))
    setMissing((prev) => prev.filter((m) => m !== id))
  }, [])

  const toggleMulti = useCallback((id: string, option: string) => {
    setAnswers((prev) => {
      const current = prev[id] ? prev[id].split(',').map((s) => s.trim()).filter(Boolean) : []
      const next = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option]
      return { ...prev, [id]: next.join(', ') }
    })
    setMissing((prev) => prev.filter((m) => m !== id))
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    const unanswered = questions
      .filter((q) => q.is_required && !(answers[q.id] ?? '').trim())
      .map((q) => q.id)
    if (unanswered.length > 0) {
      setMissing(unanswered)
      // Scrolling beats a modal here: the person needs to see which question,
      // not be told that one exists.
      document.getElementById(`q-${unanswered[0]}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    if (inFlight.current) return
    inFlight.current = true
    setSubmitting(true)
    const { data: res, error } = await supabase.rpc('empfb_submit_questionnaire', {
      p_token: token,
      p_answers: questions.map((q) => ({ question_id: q.id, value: answers[q.id] ?? '' })),
      p_opt_out: unsubscribe,
    })
    setSubmitting(false)
    inFlight.current = false

    if (error) {
      setSubmitError('Trimiterea nu a reușit. Verifică conexiunea și încearcă din nou.')
      return
    }
    const result = res as SubmitResult
    if (result.status === 'empty') {
      setSubmitError('Completează cel puțin o întrebare înainte de a trimite.')
      return
    }
    if (result.status !== 'ok') {
      setData({ status: result.status })
      return
    }
    setDone(result)
  }

  // ── States that are not the form ───────────────────────────────────
  if (loading) {
    return (
      <Shell>
        <div className="py-16 text-center text-slate-500 text-sm">Se încarcă chestionarul…</div>
      </Shell>
    )
  }

  if (loadError) {
    return <Message title="Ceva nu a mers" body={loadError} />
  }

  if (done) {
    return (
      <Shell hospital={data?.hospital_name} showLogo={data?.show_logo}>
        <div className="text-center pt-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-emerald-50 border-2 border-emerald-200">
            <svg className="h-8 w-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="mt-5 text-xl sm:text-2xl font-bold text-slate-900">
            {done.title || 'Mulțumim!'}
          </h1>
          {done.description && (
            <p className="mt-2 text-sm sm:text-base text-slate-600">{done.description}</p>
          )}
        </div>

        <div className="space-y-4 mt-6">
          {done.top_text && <UiCopyBlock text={done.top_text} />}
          {done.bottom_text && <UiCopyBlock text={done.bottom_text} />}
        </div>

        <p className="mt-8 text-center text-xs text-slate-400 leading-relaxed">
          Răspunsurile tale sunt anonime. Nu se păstrează nicio legătură între ele și numărul tău de telefon.
        </p>
      </Shell>
    )
  }

  if (data && data.status !== 'ok') {
    const copy = NOT_OK_COPY[data.status] ?? NOT_OK_COPY.not_found
    return <Message title={copy.title} body={copy.body} />
  }

  // ── The form ───────────────────────────────────────────────────────
  return (
    <Shell hospital={data?.hospital_name} showLogo={data?.show_logo}>
      <div className="pt-2">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 text-center">
          {data?.title || 'Chestionar de satisfacție'}
        </h1>
        {data?.description && (
          <p className="mt-2 text-sm sm:text-base text-slate-600 text-center">{data.description}</p>
        )}
      </div>

      <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
        <p className="text-xs sm:text-sm text-emerald-800 leading-relaxed text-justify">
          Răspunsurile sunt <strong>anonime</strong>. Se păstrează doar funcția și departamentul, ca
          rezultatele să poată fi citite pe categorii — niciodată numărul tău de telefon.
        </p>
      </div>

      {data?.top_text && <div className="mt-4"><UiCopyBlock text={data.top_text} /></div>}

      <form onSubmit={handleSubmit} className="mt-6 space-y-7">
        {questions.map((q, i) => (
          <div key={q.id} id={`q-${q.id}`} className="scroll-mt-6">
            <p className="text-sm sm:text-base font-semibold text-slate-800 text-justify hyphens-auto">
              <span className="text-slate-400 font-normal mr-1.5">{i + 1}.</span>
              {q.label}
              {q.is_required && <span className="text-rose-500 ml-1">*</span>}
            </p>

            {missing.includes(q.id) && (
              <p className="mt-1.5 text-xs text-rose-600">Această întrebare este obligatorie.</p>
            )}

            <QuestionInput
              question={q}
              value={answers[q.id] ?? ''}
              onChange={(v) => setAnswer(q.id, v)}
              onToggle={(opt) => toggleMulti(q.id, opt)}
            />
          </div>
        ))}

        {data?.bottom_text && <UiCopyBlock text={data.bottom_text} />}

        {submitError && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
            {submitError}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-3.5 rounded-xl transition-colors touch-manipulation"
        >
          {submitting ? 'Se trimite…' : 'Trimite răspunsurile'}
        </button>

        {/* Below the button, and quiet: filling the form is the point, the
            opt-out is an afterthought decision. Same sizing as the patient
            questionnaire, which ops asked to be made smaller twice. */}
        <label className="flex items-start gap-2 cursor-pointer pt-1">
          <input
            type="checkbox"
            checked={unsubscribe}
            onChange={(e) => setUnsubscribe(e.target.checked)}
            className="mt-0.5 w-3.5 h-3.5 flex-shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer touch-manipulation"
          />
          <div>
            <span className="text-[11px] sm:text-xs text-slate-500 leading-snug">
              Nu mai doresc să primesc chestionare pentru angajați.
            </span>
            <p className="text-[10px] text-slate-400 mt-0.5 leading-relaxed">
              Nu afectează mesajele pe care le primești ca pacient.
            </p>
          </div>
        </label>
      </form>

      <p className="text-center text-slate-400 text-xs pb-8 pt-8">
        © 2026 Powered by AXA Medis. Toate drepturile rezervate.
      </p>
    </Shell>
  )
}

// ── Pieces ───────────────────────────────────────────────────────────

function Shell({
  children, hospital, showLogo = true,
}: { children: React.ReactNode; hospital?: string; showLogo?: boolean }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 mt-6 px-5 sm:px-8 pt-8">
          {hospital && (
            <div className="text-center pb-4 border-b border-slate-100">
              <p className="text-lg sm:text-xl font-bold text-slate-800">{hospital}</p>
              {showLogo && (
                <img src="/logo.png" alt="" className="h-10 mx-auto mt-3 object-contain" />
              )}
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  )
}

function Message({ title, body }: { title: string; body: string }) {
  return (
    <Shell>
      <div className="py-10 text-center">
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        <p className="mt-3 text-sm text-slate-600 max-w-sm mx-auto leading-relaxed">{body}</p>
      </div>
      <p className="text-center text-slate-400 text-xs pb-8 pt-6">
        © 2026 Powered by AXA Medis.
      </p>
    </Shell>
  )
}

function QuestionInput({
  question, value, onChange, onToggle,
}: {
  question: Question
  value: string
  onChange: (v: string) => void
  onToggle: (option: string) => void
}) {
  switch (question.type) {
    case 'text':
      return (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="mt-3 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm sm:text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Răspunsul tău…"
        />
      )

    case 'yesno':
      return (
        <div className="space-y-3 mt-3">
          {['Da', 'Nu'].map((option) => (
            <Choice
              key={option}
              label={option}
              selected={value === option}
              onSelect={() => onChange(option)}
            />
          ))}
        </div>
      )

    case 'rating':
      return (
        <div className="space-y-2">
          <div className="flex justify-between items-center bg-slate-50 p-2 rounded-xl border border-slate-100 mt-3 gap-1 sm:gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => onChange(String(n))}
                className={`flex-1 min-h-[48px] max-w-[64px] rounded-lg flex items-center justify-center font-semibold text-base transition-all touch-manipulation active:scale-95 ${
                  value === String(n)
                    ? 'bg-blue-600 text-white shadow-md scale-105'
                    : 'text-slate-500 hover:bg-white hover:text-blue-600'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-[10px] sm:text-xs text-slate-400 px-1 uppercase tracking-wider font-medium">
            <span>Nesatisfăcut</span>
            <span>Satisfăcut</span>
          </div>
        </div>
      )

    case 'nps':
      return (
        <div className="mt-3 grid grid-cols-6 sm:grid-cols-11 gap-1.5">
          {Array.from({ length: 11 }, (_, n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(String(n))}
              className={`min-h-[44px] rounded-lg font-semibold text-sm transition-all touch-manipulation active:scale-95 ${
                value === String(n)
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-slate-50 border border-slate-200 text-slate-500 hover:text-blue-600'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      )

    case 'multichoice': {
      const selected = value ? value.split(',').map((s) => s.trim()).filter(Boolean) : []
      return (
        <div className="space-y-3 mt-3">
          {question.options.map((option) => (
            <Choice
              key={option}
              label={option}
              selected={selected.includes(option)}
              multiple={question.allow_multiple_selection}
              onSelect={() =>
                question.allow_multiple_selection ? onToggle(option) : onChange(option)
              }
            />
          ))}
        </div>
      )
    }

    default:
      return null
  }
}

function Choice({
  label, selected, onSelect, multiple = false,
}: { label: string; selected: boolean; onSelect: () => void; multiple?: boolean }) {
  return (
    <label className="flex items-center gap-4 cursor-pointer group py-3 px-4 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 active:bg-slate-200 transition-colors touch-manipulation">
      <span
        className={`w-6 h-6 ${multiple ? 'rounded-md' : 'rounded-full'} border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
          selected ? 'border-blue-600 bg-blue-50' : 'border-slate-300 group-hover:border-blue-400'
        }`}
      >
        {selected && (
          multiple
            ? <svg className="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            : <span className="w-3 h-3 bg-blue-600 rounded-full" />
        )}
      </span>
      <input type="checkbox" checked={selected} onChange={onSelect} className="hidden" />
      <span className={`text-base font-medium flex-1 text-left ${selected ? 'text-slate-900' : 'text-slate-600'}`}>
        {label}
      </span>
    </label>
  )
}
